"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ZoneLink } from "@/components/layout/zone-link";
import { useSession } from "@/lib/auth/client";
import { useHydrated } from "@/lib/hooks/use-hydrated";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { authHref } from "@/lib/jobs/auth-return";
import { useAuthLinkClick } from "@/lib/jobs/use-auth-link";
import { fetchNavbarData } from "@/lib/user/navbar-data-client";
import { ProfileDropdown } from "./profile-dropdown";
import { StreakHeartbeat } from "./streak-heartbeat";

type NavbarAuthProps = {
	locale: Locale;
	t: Dictionary;
};

type NavbarData = {
	streak: number;
	isPro: boolean;
};

export function NavbarAuth({ locale, t }: NavbarAuthProps) {
	const { data: session, isPending } = useSession();
	const pathname = usePathname();
	const authLinkClick = useAuthLinkClick(locale);
	const [navData, setNavData] = useState<NavbarData | null>(null);
	// Mount gate — see use-hydrated.ts for why this is needed (same fix as
	// MobileNavMenu's account section, #2608 audit T2.4).
	const hydrated = useHydrated();

	const userId = session?.user?.id;

	useEffect(() => {
		if (!userId) {
			setNavData(null);
			return;
		}
		fetchNavbarData()
			.then((data) => {
				if (data?.authenticated) {
					setNavData({
						streak: data.streak ?? 0,
						isPro: data.isPro ?? false,
					});
				}
			})
			.catch(() => {
				setNavData({ streak: 0, isPro: false });
			});
	}, [userId]);

	const guestLinks = (placeholder: boolean) => (
		<div
			className={`flex shrink-0 items-center gap-2${placeholder ? " invisible" : ""}`}
			aria-hidden={placeholder ? true : undefined}
		>
			<ZoneLink
				href={authHref("login", locale, pathname)}
				onClick={authLinkClick}
				className="inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-2xl px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			>
				{t.login}
			</ZoneLink>
			<ZoneLink
				href={authHref("signup", locale, pathname)}
				onClick={authLinkClick}
				className="inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-2xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			>
				{t.signup || (locale === "id" ? "Daftar" : "Sign up")}
			</ZoneLink>
		</div>
	);

	// While the session resolves, hold the guest buttons' exact box, hidden.
	// The old fixed-size pulse block was 96px wide against the guests' ~156px
	// buttons, so the right of the header jumped once the session settled
	// (prod layout-shift trace 2026-11-02: 0.014 on every /jobs load).
	// `invisible` keeps the links out of the tab order and the a11y tree.
	if (!hydrated || isPending) return guestLinks(true);

	if (!session?.user) return guestLinks(false);

	const isEn = locale === "en";
	const currentStreak = navData?.streak ?? 0;
	const isPro = navData?.isPro ?? false;
	// Weekly milestone (7, 14, 21…) gets one celebratory line instead of the
	// generic "Streak: N days" — the "7 hari berturut-turut" class of copy
	// from docs/plans/copy-voice-overhaul-2026-08-17.md T10.
	const isStreakMilestone = currentStreak > 0 && currentStreak % 7 === 0;
	const streakMilestoneLabel = isStreakMilestone
		? (
				t.streakMilestone ||
				(isEn ? "{count} days in a row" : "{count} hari berturut-turut")
			).replace("{count}", String(currentStreak))
		: undefined;

	return (
		<>
			<StreakHeartbeat />
			<ProfileDropdown
				email={session.user.email ?? ""}
				currentStreak={currentStreak}
				streakLabel={t.streak || (isEn ? "Streak" : "Streak")}
				streakMilestoneLabel={streakMilestoneLabel}
				streakUnitDays={
					t.streakDays ||
					(isEn ? (currentStreak === 1 ? "day" : "days") : "hari")
				}
				logoutLabel={t.logout || (isEn ? "Log out" : "Keluar")}
				loggingOutLabel={
					t.loggingOut || (isEn ? "Logging out…" : "Sedang keluar…")
				}
				menuLabel={t.profileMenu || (isEn ? "Account menu" : "Menu akun")}
				locale={locale}
				trackerLabel={t.tracker || (isEn ? "Tracker" : "Pelacak")}
				resumeLabel={t.resumeNav || "Resume"}
				alertsLabel={t.alerts || (isEn ? "Alerts" : "Notifikasi")}
				isPro={isPro}
			/>
		</>
	);
}
